import { useEffect, useMemo, useState } from "react";
import { listReviews, saveReview } from "../api/client";
import {
  applyBuyerPortalSubmission,
  buyerPortalToken,
  createPortalSubmission
} from "../domain/buyerPortal";
import { sha256Hex } from "../domain/hash";
import { formatMoney } from "../domain/money";
import { TRUST_BOUNDARY } from "../domain/constants";
import type { BuyerFundingReview, BuyerPortalSubmission } from "../domain/types";

type PortalForm = {
  buyerName: string;
  buyerEmail: string;
  acknowledgedInstructions: boolean;
  sourceEvidenceType: BuyerPortalSubmission["sourceEvidenceType"];
  sourcePartyName: string;
  sourceBankName: string;
  sourceAccountLast4: string;
  sourceFileName: string;
  sourceFileHash: string;
  paymentReceiptType: BuyerPortalSubmission["paymentReceiptType"];
  amountSent: string;
  dateSent: string;
  traceReference: string;
  paymentFileName: string;
  paymentFileHash: string;
  thirdPartyExplanation: string;
  conflictReported: boolean;
  conflictDescription: string;
  buyerNote: string;
};

function parsePortalHash(): { reviewId: string; token: string } | null {
  const parts = window.location.hash.split("/");
  if (parts.length < 5 || parts[2] !== "buyer") return null;
  const reviewId = decodeURIComponent(parts[3] || "");
  const token = decodeURIComponent(parts[4] || "");
  if (!reviewId || !token) return null;
  return { reviewId, token };
}

export function BuyerPortalRoute() {
  const [reviews, setReviews] = useState<BuyerFundingReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [notice, setNotice] = useState("");
  const portal = parsePortalHash();

  useEffect(() => {
    listReviews().then((loaded) => {
      setReviews(loaded);
      setLoading(false);
    });
  }, []);

  const review = useMemo(
    () => reviews.find((candidate) => candidate.id === portal?.reviewId),
    [reviews, portal?.reviewId]
  );

  const [form, setForm] = useState<PortalForm | null>(null);

  useEffect(() => {
    if (!review) return;
    setForm({
      buyerName: review.buyerParties[0]?.name || "",
      buyerEmail: "",
      acknowledgedInstructions: false,
      sourceEvidenceType: "bank_statement",
      sourcePartyName: review.buyerParties[0]?.name || "",
      sourceBankName: "",
      sourceAccountLast4: "",
      sourceFileName: "",
      sourceFileHash: "",
      paymentReceiptType: "wire_receipt",
      amountSent: String((review.expectedFunds.amount || review.expectedAmount)?.amountCents ?? 0),
      dateSent: "",
      traceReference: "",
      paymentFileName: "",
      paymentFileHash: "",
      thirdPartyExplanation: "",
      conflictReported: false,
      conflictDescription: "",
      buyerNote: ""
    });
  }, [review]);

  if (loading) {
    return <PortalShell><p className="text-sm text-[#596158]">Loading funding request…</p></PortalShell>;
  }

  if (!portal || !review || portal.token !== buyerPortalToken(review)) {
    return (
      <PortalShell>
        <section className="panel">
          <p className="label">Buyer funding request</p>
          <h1 className="mt-2 text-2xl font-bold">Secure access</h1>
          <p className="mt-3 text-sm text-[#596158]">
            This buyer upload link is not valid for the current review. Ask the office for a fresh scoped link.
          </p>
        </section>
      </PortalShell>
    );
  }

  if (!form) return null;

  const update = <K extends keyof PortalForm>(key: K, value: PortalForm[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  async function hashFile(file: File, prefix: "source" | "payment") {
    const hash = await sha256Hex(`${file.name}:${file.size}:${file.lastModified}`);
    if (prefix === "source") {
      update("sourceFileName", file.name);
      update("sourceFileHash", hash);
    } else {
      update("paymentFileName", file.name);
      update("paymentFileHash", hash);
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!form || !review || !portal) return;
    const submission = createPortalSubmission(review, portal.token, {
      buyerName: form.buyerName,
      buyerEmail: form.buyerEmail || undefined,
      acknowledgedInstructions: form.acknowledgedInstructions,
      sourceEvidenceType: form.sourceEvidenceType,
      sourcePartyName: form.sourcePartyName,
      sourceBankName: form.sourceBankName,
      sourceAccountLast4: form.sourceAccountLast4,
      sourceFileName: form.sourceFileName,
      sourceFileHash: form.sourceFileHash,
      paymentReceiptType: form.paymentReceiptType,
      amountSent: Number(form.amountSent) / 100 || undefined,
      dateSent: form.dateSent || undefined,
      traceReference: form.traceReference,
      paymentFileName: form.paymentFileName,
      paymentFileHash: form.paymentFileHash,
      thirdPartyExplanation: form.thirdPartyExplanation,
      conflictReported: form.conflictReported,
      conflictDescription: form.conflictDescription,
      buyerNote: form.buyerNote
    });
    const next = applyBuyerPortalSubmission(review, submission);
    await saveReview(next);
    setReviews((current) => [next, ...current.filter((item) => item.id !== next.id)]);
    setSubmitted(true);
    setNotice(
      `Your information was submitted to ${review.officeName}. The office will decide what action to take.`
    );
  }

  const expected = review.expectedFunds.amount || review.expectedAmount;

  return (
    <PortalShell>
      <section className="panel">
        <p className="label">Buyer funding request</p>
        <h1 className="mt-2 text-2xl font-bold">Buyer funding request</h1>
        <p className="mt-3 text-sm text-[#596158]">
          {review.officeName} is recording funding evidence for file {review.fileNumber}. This page helps the office
          keep the funding review in the file.
        </p>
        <p className="notice mt-4 text-sm">{TRUST_BOUNDARY}</p>
      </section>

      {notice ? (
        <section className="panel bg-[#e4efe9]" data-testid="buyer-portal-notice">
          <p className="text-sm font-semibold">{notice}</p>
        </section>
      ) : null}

      {submitted ? (
        <section className="panel">
          <h2 className="text-xl font-bold">Complete</h2>
          <p className="mt-2 text-sm text-[#596158]">
            Your submission was attached to the office review as source rows, events, and retained evidence. You can
            close this page.
          </p>
        </section>
      ) : (
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <section className="panel" data-testid="buyer-portal-instructions">
            <h2 className="text-lg font-bold">1. Review instructions</h2>
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="font-bold">Expected amount</dt>
                <dd>{formatMoney(expected)}</dd>
              </div>
              <div>
                <dt className="font-bold">Payment deadline</dt>
                <dd>{review.fundingInstruction.deadlineAt || review.dueAt || "Not recorded"}</dd>
              </div>
              <div>
                <dt className="font-bold">Instruction version</dt>
                <dd>{review.fundingInstruction.versionLabel || "Not recorded"}</dd>
              </div>
              <div>
                <dt className="font-bold">Payment method</dt>
                <dd>{review.paymentRail.rail || review.fundingInstruction.allowedRails.join(", ")}</dd>
              </div>
            </dl>
            <label className="mt-4 flex items-start gap-3 rounded-md border border-[#d8d2c4] bg-[#fffdf8] p-3">
              <input
                data-testid="buyer-portal-ack"
                type="checkbox"
                checked={form.acknowledgedInstructions}
                onChange={(event) => update("acknowledgedInstructions", event.target.checked)}
                required
              />
              <span className="text-sm">
                I acknowledge these instructions and understand the office will decide what action to take. Do not
                rely on emailed changes unless confirmed by an office-approved method.
              </span>
            </label>
          </section>

          <section className="panel">
            <h2 className="text-lg font-bold">2. Provide source evidence</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="grid gap-1">
                <span className="field-label">Your name</span>
                <input value={form.buyerName} onChange={(e) => update("buyerName", e.target.value)} required />
              </label>
              <label className="grid gap-1">
                <span className="field-label">Email</span>
                <input type="email" value={form.buyerEmail} onChange={(e) => update("buyerEmail", e.target.value)} />
              </label>
              <label className="grid gap-1 md:col-span-2">
                <span className="field-label">Evidence type</span>
                <select
                  value={form.sourceEvidenceType}
                  onChange={(e) => update("sourceEvidenceType", e.target.value as PortalForm["sourceEvidenceType"])}
                >
                  <option value="bank_statement">Bank statement</option>
                  <option value="bank_letter">Bank letter</option>
                  <option value="voided_check">Voided check</option>
                  <option value="manual_note">Manual note</option>
                  <option value="connect_bank_later">Connect bank later (Plaid when available)</option>
                  <option value="unable_to_provide">Unable to provide</option>
                </select>
              </label>
              <label className="grid gap-1">
                <span className="field-label">Source party name</span>
                <input value={form.sourcePartyName} onChange={(e) => update("sourcePartyName", e.target.value)} />
              </label>
              <label className="grid gap-1">
                <span className="field-label">Bank name</span>
                <input value={form.sourceBankName} onChange={(e) => update("sourceBankName", e.target.value)} />
              </label>
              <label className="grid gap-1">
                <span className="field-label">Account last four</span>
                <input
                  maxLength={4}
                  value={form.sourceAccountLast4}
                  onChange={(e) => update("sourceAccountLast4", e.target.value.replace(/\D/g, "").slice(0, 4))}
                />
              </label>
              <label className="grid gap-1">
                <span className="field-label">Upload source file</span>
                <input type="file" onChange={(e) => e.target.files?.[0] && hashFile(e.target.files[0], "source")} />
              </label>
            </div>
          </section>

          <section className="panel">
            <h2 className="text-lg font-bold">3. Upload payment receipt</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="grid gap-1 md:col-span-2">
                <span className="field-label">Receipt type</span>
                <select
                  value={form.paymentReceiptType}
                  onChange={(e) => update("paymentReceiptType", e.target.value as PortalForm["paymentReceiptType"])}
                >
                  <option value="wire_receipt">Wire receipt</option>
                  <option value="ach_confirmation">ACH confirmation</option>
                  <option value="rtp_fednow_confirmation">RTP / FedNow confirmation</option>
                  <option value="cashier_check_image">Cashier check image</option>
                  <option value="bank_screenshot">Bank screenshot</option>
                  <option value="not_initiated">Not initiated</option>
                  <option value="unable_to_provide">Unable to provide</option>
                </select>
              </label>
              <label className="grid gap-1">
                <span className="field-label">Amount sent (cents)</span>
                <input value={form.amountSent} onChange={(e) => update("amountSent", e.target.value)} />
              </label>
              <label className="grid gap-1">
                <span className="field-label">Date sent</span>
                <input type="datetime-local" value={form.dateSent} onChange={(e) => update("dateSent", e.target.value)} />
              </label>
              <label className="grid gap-1">
                <span className="field-label">Trace / reference</span>
                <input value={form.traceReference} onChange={(e) => update("traceReference", e.target.value)} />
              </label>
              <label className="grid gap-1">
                <span className="field-label">Upload payment receipt</span>
                <input type="file" onChange={(e) => e.target.files?.[0] && hashFile(e.target.files[0], "payment")} />
              </label>
            </div>
          </section>

          <section className="panel">
            <h2 className="text-lg font-bold">4. Exceptions or conflicts</h2>
            <label className="mt-3 grid gap-1">
              <span className="field-label">Gift / third-party / entity / trust explanation</span>
              <textarea
                value={form.thirdPartyExplanation}
                onChange={(e) => update("thirdPartyExplanation", e.target.value)}
              />
            </label>
            <label className="mt-3 flex items-start gap-3">
              <input
                type="checkbox"
                checked={form.conflictReported}
                onChange={(e) => update("conflictReported", e.target.checked)}
              />
              <span className="text-sm">I received conflicting or different funding instructions.</span>
            </label>
            {form.conflictReported ? (
              <label className="mt-3 grid gap-1">
                <span className="field-label">Describe the conflict</span>
                <textarea
                  required
                  value={form.conflictDescription}
                  onChange={(e) => update("conflictDescription", e.target.value)}
                />
              </label>
            ) : null}
            <label className="mt-3 grid gap-1">
              <span className="field-label">Notes for the office</span>
              <textarea value={form.buyerNote} onChange={(e) => update("buyerNote", e.target.value)} />
            </label>
          </section>

          <section className="panel">
            <button className="button-primary" data-testid="buyer-portal-submit" type="submit">
              Submit funding evidence
            </button>
            <p className="mt-3 text-sm text-[#596158]">
              No payment is made through Veto. This page records evidence for the office review.
            </p>
          </section>
        </form>
      )}
    </PortalShell>
  );
}

function PortalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#f5f2ea] text-[#191919]">
      <main className="mx-auto grid max-w-3xl gap-4 p-5">{children}</main>
    </div>
  );
}
